<template>
  <div v-if="breadcrumbData" class="breadcrumb">
    <div class="breadcrumb__content--wrap">
      <div class="breadcrumb__content"
           :class="breadcrumbType">
        <button type="button" @click="onDomainListClick">
          <i class="icon icon--site-list"></i>
        </button>
        <template v-for="(item, index) in breadcrumbDepthsBefore">
          <i :key="`before-${index}`" class="icon icon--bc-step"></i>
          <router-link v-if="item.to"
                       class="breadcrumb__content-step-link"
                       :class="item.parts[0].cssClass"
                       :key="`before-link-${index}`"
                       :to="item.to">
            <span :key="`before-link-step-${index}`" class="breadcrumb__content-step"
                  :class="{ active: !breadcrumbDepthsAfter.length &&
              index === breadcrumbDepthsBefore.length - 1 }">
              <span v-for="(part, index) of item.parts" :key="`before-link-step-parts-${index}`"
                    :class="part.cssClass"
                    :title="part.name">
                {{ part.name }}
              </span>
            </span>
          </router-link>
          <span v-else
                class="breadcrumb__content-step"
                :key="`before-normal-step-${index}`"
                :class="{ active: !breadcrumbDepthsAfter.length &&
            index === breadcrumbDepthsBefore.length - 1 }">
            <span v-for="(part, index) of item.parts" :key="`before-normal-step-parts-${index}`"
                  :class="part.cssClass"
                  :title="part.name">
              {{ part.name }}
            </span>
          </span>
        </template>

        <div v-if="!breadcrumbData.hideDateRange" class="periods_panel">
          <input type="text"
                 class="periods_label"
                 readonly
                 :disabled="breadcrumbData.disableDatePicker"
                 :value="dateRangeStr"
                 @click.stop="togglePickerClick" />
          <transition name="calendar">
            <date-range-picker v-if="showDateRangePicker"
                               :timezone="applyData.timezone"
                               :timezoneName="applyData.timezoneName"
                               :minDate="calendarMinDate"
                               :maxDate="calendarMaxDate"
                               :dateRange="dateRange"
                               :maxDays=maxDays
                               @onRangeCancel="onRangeCancel"
                               @onRangeChange="onRangeChange" />
          </transition>
          <i class="icon icon--calendar"></i>
        </div>
        <div class="campaign_targeting"
             v-if="canShowCampaignTargeting">
          {{ $t('campaignTargeting.title') }}
          <ui-switch :on="hasCampaign || showCampaignTargeting"
                     :onValue="' '"
                     :offValue="' '"
                     @click="toggleCampaign()"/>
          <i class="icon icon--setting"
             v-if="hasCampaign || showCampaignTargeting"
             @click="toggleCampaign(true)"></i>
          <div class="current-campaign"
               v-if="hasCampaign">
            <div class="utm"
                 v-if="campaign.utm !== null">
              <div class="campaign-header">
                  <span class="name"
                        v-html="$t('campaignTargeting.utm.name')"></span>
                <span class="label">ON</span>
              </div>
              <div class="campaign-content">
                <ul>
                  <template v-for="tag in utmTags">
                    <li :key="`currentUtm${tag}`">
                        <span class="name"
                              v-html="$t('campaignTargeting.utm.' + tag)"></span>
                      <span class="count">
                          {{ campaign.utm[tag] ? campaign.utm[tag].length : 0 | suffixPlus }}
                        </span>
                    </li>
                  </template>
                </ul>
              </div>
            </div>
          </div>
        </div>
        <div class="user_test" v-if="hasUtList && canShowUserTest && !isDemo">
          {{ $t('userTest.title') }}
          <ui-switch :on="hasUtId || showUserTest"
                     :onValue="' '"
                     :offValue="' '"
                     @click="toggleUt()"/>
          <div class="ut-list">
            <user-test v-if="showUserTest"
                       :ut-list="utList"
                       @onClose="onCloseUtList" />
          </div>
        </div>

        <template v-for="(item, index) in breadcrumbDepthsAfter">
          <i :key="`after-${index}`" class="icon icon--bc-step"></i>
          <span :key="`after-span-${index}`" class="breadcrumb__content-step"
                :class="{ active: index === breadcrumbDepthsAfter.length - 1 }">{{ item }}</span>
        </template>
      </div>
    </div>
    <div class="breadcrumb__tab">
      <i class="icon icon--bc-tab-btn"
         @click.stop="onGnbMinimize"></i>
    </div>

    <dimmed v-if="showCampaignTargeting">
      <campaign-targeting :aid="aid"
                          :startDate="startDate"
                          :endDate="endDate"
                          :campaign="campaign"
                          :utmCache="utmCache"
                          @onClose="showCampaignTargeting = false" />
    </dimmed>
  </div>
</template>

<script>
import { parse, format } from 'date-fns'
import { mapState, mapMutations } from 'vuex'
import { create } from 'vue-modal-dialogs'
import { UTM_TAGS } from '@/libs/constants'
import { EventBus } from '@/libs/event-bus'

import { triggerClickEvent, browserInfo, dateFormatTimezone } from '@/libs/utils'
import { getUtid } from '@/api/report'

import AppDialog from '@/components/ui/AppDialog'
import UiSwitch from '@/components/ui/UiSwitch'
import Dimmed from '@/components/ui/Dimmed'

import DateRangePicker from '@/components/date-picker/DateRangePicker'
import CampaignTargeting from '@/components/report/campaign-targeting/index'
import UserTest from '@/components/report/user-test/index'

const openDialog = create(AppDialog)

export default {
  name: 'ReportHeader',
  components: {
    UiSwitch,
    Dimmed,
    DateRangePicker,
    CampaignTargeting,
    UserTest
  },
  data () {
    return {
      items: ['4Grit Team', 'Reporting Heatmap'],
      showDateRangePicker: false,
      showCampaignTargeting: false,
      campaign: null,
      utmCache: null,
      utmTags: [],
      showUserTest: false,
      utList: [],
      utid: null,
      utEnabled: false,
      maxDays: 31
    }
  },
  computed: {
    ...mapState([
      'userData',
      'applyData',
      'abPageName',
      'funnelData',
      'showReportPageList',
      'reportPeriod',
      'ctaData',
      'isDemo'
    ]),
    aid () {
      return this.$route.params.aid
    },
    startDate () {
      return dateFormatTimezone(this.dateRange.startDate, this.applyData.timezoneName, 'YYYY-MM-DD')
    },
    endDate () {
      return dateFormatTimezone(this.dateRange.endDate, this.applyData.timezoneName, 'YYYY-MM-DD')
    },
    getTimezone () {
      return `(${this.applyData.timezone}) ${this.applyData.timezoneName}`
    },
    dateRange () {
      return {
        startDate: parse(this.reportPeriod.start),
        endDate: parse(this.reportPeriod.end)
      }
    },
    dateRangeStr () {
      return `${format(this.dateRange.startDate, 'MMM DD, YYYY')} - ${format(this.dateRange.endDate, 'MMM DD, YYYY')}`
    },
    hasCampaign () {
      return !!this.campaign && !!this.campaign.utm
    },
    hasUtList () {
      return this.utList && this.utList.length > 0
    },
    hasUtId () {
      return !!this.utid
    },
    breadcrumbType () {
      if (this.$route.name === 'cta-report') {
        if (this.ctaData && this.ctaData.coords) {
          return 'breadcrumb-cta-except'
        }
      }
      return null
    },
    breadcrumbData () {
      return this.$route.meta.breadcrumb
    },
    breadcrumbDepths () {
      const data = {
        before: [],
        after: []
      }

      if (this.breadcrumbData) {
        if (this.breadcrumbData.depths) {
          data.before = this.breadcrumbData.depths.map(item => {
            switch (item) {
              case ':domain':
                return {
                  parts: [
                    {
                      name: this.applyData.real_domain,
                      cssClass: 'domain-name'
                    }
                  ]
                }
              case ':pageName':
                return {
                  parts: [
                    {
                      name: this.applyData.pageName,
                      cssClass: 'page-name'
                    }
                  ]
                }
              case ':abName':
                return {
                  parts: [
                    {
                      name: this.abPageName,
                      cssClass: 'ab-name'
                    }
                  ]
                }
              case ':funnelName':
                return {
                  parts: [
                    {
                      name: this.funnelData.name,
                      cssClass: 'funnel-name'
                    }
                  ]
                }
              case ':funnelSessionStep':
                const seq = Number(this.$route.query.seq)
                return {
                  parts: [
                    {
                      name: `Step ${seq + 1}`
                    }
                  ]
                }
              case '@A/B Testing':
                return {
                  parts: [
                    {
                      name: 'A/B Testing'
                    }
                  ],
                  to: { name: 'ab.list', params: { aid: this.aid } }
                }
              case '@Funnels':
                return {
                  parts: [
                    {
                      name: 'Funnels'
                    }
                  ],
                  to: { name: 'funnel.list', params: { aid: this.aid } }
                }
              case '@funnelName':
                return {
                  parts: [
                    {
                      name: this.funnelData.name,
                      cssClass: 'funnel-name'
                    }
                  ],
                  to: {
                    name: 'funnel.view',
                    params: {
                      aid: this.aid,
                      fid: this.$route.params.fid,
                      timezoneName: this.applyData.timezoneName
                    },
                    query: {
                      device: this.$route.query.device,
                      faid: this.$route.query.faid,
                      timezoneName: this.$route.query.timezoneName
                    }
                  }
                }
              default:
                if (item) {
                  return {
                    parts: [
                      {
                        name: this.$t(`breadcrumb.${item}`)
                      }
                    ]
                  }
                }
                return null
            }
          }).filter(item => !!item)
        }
        if (this.breadcrumbData.depthsAfter) {
          data.after = this.breadcrumbData.depthsAfter.map(item => {
            switch (item) {
              case ':ctaExcept':
                if (this.ctaData && this.ctaData.coords) {
                  return this.$t('common.analyzeResult')
                } else {
                  return null
                }
            }
          }).filter(item => !!item)
        }
      }
      return data
    },
    breadcrumbDepthsBefore () {
      return this.breadcrumbDepths.before
    },
    breadcrumbDepthsAfter () {
      return this.breadcrumbDepths.after
    },
    calendarMinDate () {
      return parse(this.reportPeriod.min)
    },
    calendarMaxDate () {
      return parse(this.reportPeriod.max)
    },
    canShowCampaignTargeting () {
      return this.$route.name === 'heatmap-report' || this.$route.name === 'cta-report'
    },
    canShowUserTest () {
      return this.$route.name === 'heatmap-report'
    }
  },
  methods: {
    ...mapMutations([
      'setGnbMinimize',
      'setShowReportPageList',
      'setReportPeriod'
    ]),
    togglePageList () {
      this.setShowReportPageList(!this.showReportPageList)
    },
    togglePickerClick () {
      EventBus.$emit('closeAllUiSelectBox')
      EventBus.$emit('closeUserMenu')
      EventBus.$emit('closeReferrerList')
      EventBus.$emit('closeReportPageList')
      if (!this.showDateRangePicker) {
        triggerClickEvent()
      }
      this.showDateRangePicker = !this.showDateRangePicker
    },
    closePicker () {
      this.showDateRangePicker = false
    },
    toggleCampaign (force) {
      if (force) {
        this.showCampaignTargeting = true
      } else {
        if (this.hasCampaign) {
          EventBus.$emit('onCampaignChange', null)
        } else {
          this.showCampaignTargeting = true
        }
      }
    },
    toggleUt () {
      if (this.hasUtId) {
        this.utid = null
        EventBus.$emit('onUtChange', null)
        this.utEnabled = false
      } else {
        this.showUserTest = true
      }
    },
    onCloseUtList (utid) {
      this.showUserTest = false
      if (utid) {
        this.utid = utid
        EventBus.$emit('onUtChange', utid)
      }
    },
    onRangeCancel () {
      this.closePicker()
    },
    onRangeChange (data) {
      if (this.hasCampaign) {
        openDialog({
          dialogType: 'report.changePeriodResetUtm',
          ok: this.$t('common.yes'),
          cancel: this.$t('common.no')
        }).then(result => {
          if (result) {
            const range = data.range
            this.setReportPeriod({
              ...this.reportPeriod,
              start: format(range.startDate, 'YYYY-MM-DD'),
              end: format(range.endDate, 'YYYY-MM-DD')
            })
            this.closePicker()
            if (!this.campaign) {
              this.campaign = {}
            }
            this.campaign.utm = null
          }
        })
      } else {
        this.utmCache = null
        const range = data.range
        this.setReportPeriod({
          ...this.reportPeriod,
          start: format(range.startDate, 'YYYY-MM-DD'),
          end: format(range.endDate, 'YYYY-MM-DD')
        })
        this.closePicker()
      }
    },
    onGnbMinimize () {
      this.setGnbMinimize()
    },
    onDomainListClick () {
      const sid = this.applyData.sid
      this.$router.push({ name: 'dashboard', query: { sid } })
    }
  },
  created () {
    this.utmTags = UTM_TAGS

    let body = document.querySelector('body')
    body.classList.add(browserInfo.name)

    const sixMonthsExpandIds = process.env.VUE_APP_6MONTHS_IDS ? process.env.VUE_APP_6MONTHS_IDS.split(',') : null

    if (sixMonthsExpandIds.includes(this.userData.userId)) {
      this.maxDays = 200
    }

    if (location.hostname !== 'skt.beusable.net') {
      getUtid(this.applyData.sid).then(result => {
        if (result && result.length > 0) {
          this.utList = result
        }
      })
    }
  },
  mounted () {
    EventBus.$on('closeDateRangePicker', () => {
      this.showDateRangePicker = false
    })
    EventBus.$on('onCampaignChange', (campaign, resetCache) => {
      if (resetCache) {
        this.utmCache = null
      } else {
        this.utmCache = this.campaign ? this.campaign.utm : null
      }
      this.campaign = campaign
    })
    EventBus.$on('onUtButtonChange', (utid) => {
      this.utid = utid
    })
  },
  destroyed () {
    let body = document.querySelector('body')
    body.classList.remove(browserInfo.name)
  }
}
</script>

<style lang="scss">
.breadcrumb {
  position: relative;
  height: 51px;

  .breadcrumb__content--wrap{
    margin-right: 100px;
    height: 81px;
    padding-left: 30px;
    box-sizing: border-box;
    background: url(../../assets/img/breadcrumb_bg.png) repeat 0 0;
    background-size: contain;
    white-space: nowrap;
    position: relative;

    .breadcrumb__content{
      height: 48px;
      box-sizing: border-box;

      & > * {
        display: inline-block;
        vertical-align: middle;
        height: 100%;

        &~a {
          line-height: 50px;
        }
      }

      .icon--site-list {
        vertical-align: middle;
        position: relative;

        &:hover:after {
          @include positionXYcenter;
          display: block;
          content: '';
          width: 32px;
          height: 32px;
          background-color: rgba(0, 0, 0, 0.06);
          border-radius: 50%;
        }
      }
    }

    .breadcrumb__content-step {
      font-size: 14px;
      color: #767676;
      white-space: nowrap;
      display: inline;

      &.active {
        color: #444444;
        font-weight: 600;
      }

      .page-name,
      .domain-name,
      .funnel-name,
      .ab-name {
        vertical-align: text-bottom;
        white-space: nowrap;
        display: inline-block;
        text-overflow: ellipsis;
        overflow: hidden;
        line-height: 1.2;
        font-weight: 500;
      }

      .page-name {
        max-width: 33%;
      }
      .domain-name {
        max-width: 17%;
      }
      .ab-name {
        max-width: 20%;
      }
    }

    .breadcrumb__content-step-link {
      &.funnel-name {
        max-width: 30%;

        span {
          display: inline-block;
          width: 100%;
        }
      }
      &:hover {
        .breadcrumb__content-step {
          color: #1199af;

          &.active {
            color: #1199af;
          }
        }
      }
    }

    .periods_panel {
      margin-left: 10px;
      position: relative;
      top: 1px;
      height: 32px;
      border-radius: 8px;
      border: solid 1px #e0e0e0;
      background-color: #ffffff;

      width: 205px;
      box-sizing: border-box;
      display: inline-block;

      .periods_label {
        cursor: pointer;
        width: 100%;
        height: 100%;
        padding-left: 10px;
        line-height: 28px;
        background-color: transparent;
        border: none;
        box-sizing: border-box;
        font-size: 13px;
        color: #767676;

        &[disabled] {
          cursor: default;
        }
      }

      .icon--calendar {
        position: absolute;
        top: 50%;
        right: 8px;
        transform: translateY(-50%);
        pointer-events: none;
      }
    }
  }

  .user_test {
    position: relative;
    top: 12px;
    font-size: 13px;
    font-weight: 600;
    line-height: 1.08;
    color: #444444;
    height: 34px;
    white-space: nowrap;
    overflow: visible;

    &::before {
      content: "";
      display: inline-block;
      width: 1px;
      height: 13px;
      background-color: #d5d5d5;
      vertical-align: middle;
      margin: 0 7px 0 10px;
    }

    .ui-switch {
      vertical-align: middle;
      margin: 0 0 1px 3px;
    }

    .ut-list {
      position: absolute;
      top: 31px;
      left: 50%;
      transform: translateX(-50%);
    }
  }

  .campaign_targeting {
    position: relative;
    top: 12px;
    font-size: 13px;
    font-weight: 600;
    line-height: 1.08;
    color: #444444;
    height: 34px;
    white-space: nowrap;
    overflow: visible;

    &::before {
      content: "";
      display: inline-block;
      width: 1px;
      height: 13px;
      background-color: #d5d5d5;
      vertical-align: middle;
      margin: 0 6px 0 10px;
    }

    .ui-switch {
      vertical-align: middle;
      margin: 0 1px 0 2px;
    }

    .icon {
      vertical-align: middle;

      &.icon--setting {
        cursor: pointer;
      }
    }

    .current-campaign {
      position: absolute;
      top: 35px;
      left: 115px;
      box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.08);
      background-color: #ffffff;
      border-radius: 6px;
      transition: opacity 0.15s ease,
      transform 0s ease 0.2s;
      opacity: 0;
      transform: scaleY(0);
      transform-origin: top;

      &::before {
        content: '';
        background-color: #fff;
        position: absolute;
        z-index: 2;
        transform: rotate(-45deg);
        width: 10px;
        height: 10px;
        box-shadow: 1px -1px 0 0 rgba(0, 0, 0, 0.09);
        top: -4px;
        left: 14px;
      }

      .campaign-header {
        border-bottom: 1px dotted #ddd;
        margin: 9px;
        padding: 6px 0 8px 0;
        font-size: 12px;
        line-height: 1.33;

        .name {
          width: 69px;
          white-space: normal;
          vertical-align: middle;
          display: inline-block;
        }

        .label {
          padding: 2px 0;
          width: 24px;
          text-align: center;
          border-radius: 10px;
          background-color: #3eaa08;
          font-size: 10px;
          color: #ffffff;
          vertical-align: middle;
          display: inline-block;
        }
      }

      .campaign-content {
        padding-bottom: 3px;
        pointer-events: none;

        li {
          color: #666666;
          line-height: 1.17;
          font-size: 12px;
          padding: 0 9px 11px 9px;

          .count {
            color: #ec0047;
            float: right;
            font-size: 11px;
            display: inline-block;
            margin-top: 1px;
            margin-right: 3px;
          }
        }
      }
    }

    &:hover {
      overflow: visible;

      .current-campaign {
        opacity: 1;
        transform: scaleY(1);
        transition: opacity 0.15s ease;
      }
    }
  }

  .breadcrumb__tab {
    position: absolute;
    top: 0;
    right: 0;

    display: inline-block;
    width: 100px;
    height: 81px;
    background: url(../../assets/img/breadcrumb_tab_bg.png);
    background-size: contain;

    i {
      position: absolute;
      left: 50%;
      bottom: 23px;
      transform: translateX(-50%);
      padding-top: 10px;
      padding-left: 10px;
      padding-right: 10px;
      background-position: 10px 10px;
    }
  }

  .calendar-enter-active,
  .calendar-leave-active {
    transform-origin: top;
    transition: transform 0.3s ease,
    opacity 0.3s ease,
    height 0.3s ease !important;
  }

  .calendar-enter,
  .calendar-leave-to{
    transform: translate(-50%, -30px) scaleY(0.5) !important;
    opacity: 0;
  }
}

.ie {
  .breadcrumb .breadcrumb__content--wrap .breadcrumb__content-step {
    .page-name {
      vertical-align: text-top;
    }
  }
}
</style>
